import { useMutation } from "@apollo/client";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { ReviewerPicker } from "components/Submission/ReviewersEditList";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  SetSubmissionAssigneesMutation,
  SetSubmissionAssigneesMutationVariables,
} from "generated-graphql/graphql";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { SET_SUBMISSION_ASSIGNEES_MUTATION } from "queries/submissions";
import { Row } from "./types";
import { partitionSubmissions } from "./utils";

type ChangeAssigneeDialogProps = {
  open: boolean;
  onClose: () => void;
  submissions: Row[];
  onSuccess: () => void;
};

export function ChangeAssigneeDialog({
  open,
  onClose,
  submissions,
  onSuccess,
}: ChangeAssigneeDialogProps) {
  const [selectedAssignee, setSelectedAssignee] = useState<{
    id: string;
    name: string;
    email: string;
    order: number;
    isStaff: boolean;
  } | null>(null);

  const alerts = useAlerts();

  const [setAssignees, { loading }] = useMutation<
    SetSubmissionAssigneesMutation,
    SetSubmissionAssigneesMutationVariables
  >(SET_SUBMISSION_ASSIGNEES_MUTATION, {
    refetchQueries: ["SubmissionVirtualEvents"],
  });

  const handleClose = () => {
    setSelectedAssignee(null);
    onClose();
  };

  // Partition virtual events from real submissions
  const { virtualEvents, uniqueSubmissionIds } = useMemo(
    () => partitionSubmissions(submissions),
    [submissions]
  );

  const handleSubmit = async () => {
    if (!selectedAssignee) {
      alerts.error("Please select an assignee");
      return;
    }

    if (uniqueSubmissionIds.length === 0) {
      alerts.error(
        "No real submissions selected. Virtual events must be created before assigning."
      );
      return;
    }

    try {
      const result = await setAssignees({
        variables: {
          submissionIds: uniqueSubmissionIds,
          assigneeId: selectedAssignee.id,
        },
      });

      if (result.data?.setSubmissionAssignees.succeeded) {
        const virtualCount = virtualEvents.length;
        alerts.success(
          `Successfully updated assignee for ${result.data.setSubmissionAssignees.updated} submission(s)` +
            (virtualCount > 0
              ? `. ${virtualCount} virtual event(s) skipped.`
              : "")
        );
        handleClose();
        onSuccess();
      } else {
        alerts.error("Failed to update assignees");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      alerts.error(`Failed to update assignees: ${message}`);
    }
  };

  const tableRows = useMemo(() => {
    const { realSubmissions, virtualEvents } =
      partitionSubmissions(submissions);

    const realRows = realSubmissions.map((s) => ({
      id: s.id,
      isVirtual: false,
      dueDate: s.submission?.dueDate,
      facility:
        s.facilities?.map((f: { name: string }) => f.name).join(", ") || "",
      submissionType: s.submission?.submissionType,
      title: s.submission?.title,
      currentAssignee: s.submission?.staffAssignee
        ? `${s.submission.staffAssignee.person?.first || ""} ${
            s.submission.staffAssignee.person?.last || ""
          }`.trim() || s.submission.staffAssignee.email
        : "Unassigned",
    }));

    const virtualRows = virtualEvents.map((s) => ({
      id: s.id,
      isVirtual: true,
      dueDate: s.deadline,
      facility:
        s.facilities?.map((f: { name: string }) => f.name).join(", ") || "",
      submissionType: s.submission?.submissionType,
      title: s.title,
      currentAssignee: "N/A (Virtual Event)",
    }));

    return [...realRows, ...virtualRows];
  }, [submissions]);

  const columns: CustomGridColDef<(typeof tableRows)[number]>[] = [
    {
      field: "dueDate",
      headerName: "Due Date",
      width: 100,
      valueGetter: (value, row) =>
        row.dueDate ? DateTime.fromISO(row.dueDate).toFormat("MM/dd/yyyy") : "",
    },
    {
      field: "facility",
      headerName: "Facility",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "submissionType",
      headerName: "Submission Type",
      width: 130,
      valueGetter: (value, row) => prettyPrintEnumValue(row.submissionType),
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "currentAssignee",
      headerName: "Current Assignee",
      width: 140,
    },
    {
      field: "updatedAssignee",
      headerName: "Updated Assignee",
      width: 140,
      valueGetter: (value, row) => {
        if (row.isVirtual) {
          return "Skipped";
        }
        return selectedAssignee
          ? `${selectedAssignee.name} (${selectedAssignee.email})`
          : "";
      },
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: 800,
            maxWidth: "90vw",
          },
        },
      }}
    >
      <DialogTitle>
        Change Assignee for {submissions.length} Submission(s)
        {submissions.some((s) => !s.submission?.id) && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {submissions.filter((s) => !s.submission?.id).length} virtual
            event(s) will be skipped
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to update the assignee of the selected{" "}
            {submissions.length} submission(s)?
            {submissions.some((s) => !s.submission?.id) && (
              <>
                <br />
                <strong>Note:</strong>{" "}
                {submissions.filter((s) => !s.submission?.id).length} virtual
                event(s) will be skipped because they don't exist yet. Create
                them first using "Change Status" to set an assignee.
              </>
            )}
          </Typography>

          <Box>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", mb: 1, display: "block" }}
            >
              Encamp Assignee *
            </Typography>
            <ReviewerPicker
              onChange={(reviewer) => {
                setSelectedAssignee(reviewer);
              }}
              value={selectedAssignee}
              staffOnly={true}
            />
          </Box>

          {selectedAssignee && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Submissions ({tableRows.filter((r) => !r.isVirtual).length} will
                be updated
                {tableRows.filter((r) => r.isVirtual).length > 0 &&
                  `, ${tableRows.filter((r) => r.isVirtual).length} skipped`}
                )
              </Typography>
              <DataGrid
                rows={tableRows}
                columns={columns}
                disableRowSelectionOnClick
                pagination
                paginationMode="client"
                pageSizeOptions={[5, 10]}
                hideFooterPagination={false}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 10 },
                  },
                }}
                sx={{ maxHeight: 600 }}
              />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !selectedAssignee}
        >
          {loading ? "Updating..." : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
